import { Component, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../../shared/services/apis/usuario/auth.service';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { UsuarioLoginResponse } from '../../../shared/services/responses/usuario/usuario-login.response';
import { MatDialog } from '@angular/material/dialog';
import { ModalPrimeiroAcessoComponent } from './modal-primeiro-acesso/modal-primeiro-acesso.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SuperAcessoStruct } from '../../../shared/services/structs/usuario-admin/super-acesso.struct';
import { firstValueFrom } from 'rxjs';
import { SuperAcessoService } from '../../../shared/services/apis/usuario-admin/super-acesso.service';
import { CpfCnpjPipe } from '../../../shared/pipes/cpf-cnpj.pipe';
import { EmpresaAuthStruct } from '../../../shared/services/structs/usuario/empresa-auth.struct';
import { AuthLayoutComponent } from '../../components/auth-layout/auth-layout.component';

/** Agrupamento de empresas exibido na etapa de escolha (apenas UI). */
interface GrupoEmpresaUi {
  nome: string;
  icone: string;
  empresas: EmpresaAuthStruct[];
}

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['../../auth-shared.css', './auth.component.css'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    CpfCnpjPipe,
    AuthLayoutComponent,
  ],
})
export class AuthComponent implements OnInit {
  public model: FormGroup;
  public isLoading: boolean = false;
  public mostrarListaEmpresa: boolean = false;
  public usuarioLoginResponse: UsuarioLoginResponse;
  public superAcessoResponse: SuperAcessoStruct;
  public token: string | null = null;

  hide = signal(true);

  /** Texto digitado na busca da etapa de escolha de empresa (filtro local, só UI). */
  filtroEmpresa = signal('');

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private superAcessoService: SuperAcessoService,
    private router: Router,
    private alertService: AlertService,
    public dialog: MatDialog,
    private activatedRoute: ActivatedRoute
  ) {}

  clickEvent(event: MouseEvent): void {
    this.hide.set(!this.hide());
    event.stopPropagation();
  }

  async ngOnInit(): Promise<void> {
    this.model = this.formBuilder.group({
      email: ['', Validators.required],
      senha: ['', Validators.required],
      idEmpresa: [''],
    });

    // Necessário para evitar que seja carregado duas vezes
    if (typeof window !== 'undefined') {
      this.token = this.activatedRoute.snapshot.queryParamMap.get('token');

      if (this.token) {
        const isSuccess = await this.validaTokenSuperAcesso(this.token);

        if (isSuccess) {
          this.signInSuperAcesso(this.token);
        }
        this.router.navigate(['/']);
      }
    }
  }

  signInSuperAcesso(tokenAcesso: string): void {
    this.isLoading = true;

    this.authService.signInSuperAcesso(tokenAcesso).subscribe({
      next: (response: UsuarioLoginResponse) => {
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          this.isLoading = false;
          return;
        }

        this.usuarioLoginResponse = response;
        this.authService.updateToken(response);
        this.isLoading = false;
        this.router.navigate(['/home']);
      },
      error: error => {
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
        this.isLoading = false;
      },
    });
  }

  async validaTokenSuperAcesso(tokenAcesso: string): Promise<boolean> {
    try {
      const response = await firstValueFrom(this.superAcessoService.validaSuperAcesso(tokenAcesso));

      if (response.isError) {
        this.alertService.show('Erro', response.errorDescription, AlertType.error);
        this.isLoading = false;
        return false;
      }

      this.isLoading = false;
      return true;
    } catch (error: any) {
      this.alertService.show('Erro inesperado', error.message, AlertType.error);
      this.isLoading = false;
      return false;
    }
  }

  async signIn(): Promise<void> {
    if (this.model.invalid || this.isLoading) {
      return;
    }

    this.isLoading = true;

    const idEmpresa: number | null = this.model.get('idEmpresa')?.value
      ? Number(this.model.get('idEmpresa')?.value)
      : null;

    const email: string = this.model.get('email')?.value;
    const senha: string = this.model.get('senha')?.value;

    this.authService.signIn(email, senha, idEmpresa).subscribe({
      next: (response: UsuarioLoginResponse) => {
        if (response.isError) {
          // Em caso de erro a etapa atual (credenciais ou lista de empresas) permanece na tela.
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          this.isLoading = false;
          return;
        }

        this.usuarioLoginResponse = response;

        if (response.primeiroAcesso) {
          this.mostrarListaEmpresa = false;
          this.abrirModalNovaSenha(email);
          this.isLoading = false;
          return;
        }

        if (response.escolherEmpresa) {
          this.mostrarListaEmpresa = true;
          this.isLoading = false;
          return;
        }

        this.authService.updateToken(response);
        this.isLoading = false;
        this.router.navigate(['/home']);
      },
      error: error => {
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
        this.isLoading = false;
      },
    });
  }

  abrirModalNovaSenha(email: string): void {
    const dialogRef = this.dialog.open(ModalPrimeiroAcessoComponent, {
      data: {
        email: email,
      },
      panelClass: 'dialog-add-item',
    });

    dialogRef.afterClosed().subscribe(() => {
      this.model.get('senha')?.setValue('');
    });
  }

  redirectRevoceryPassword(): void {
    this.router.navigate(['/auth/senha-recuperar']);
  }

  // ----- Etapa de escolha da empresa (apenas estado de UI) -----

  /** Volta para o formulário de credenciais mantendo email e senha preenchidos. */
  voltarParaLogin(): void {
    this.mostrarListaEmpresa = false;
    this.model.get('idEmpresa')?.setValue('');
    this.filtroEmpresa.set('');
  }

  selecionarEmpresa(empresa: EmpresaAuthStruct): void {
    this.model.get('idEmpresa')?.setValue(empresa.idEmpresa);
  }

  get idEmpresaSelecionada(): number | null {
    const valor = this.model?.get('idEmpresa')?.value;
    return valor ? Number(valor) : null;
  }

  get nomeEmpresaSelecionada(): string {
    const id = this.idEmpresaSelecionada;
    const empresa = this.usuarioLoginResponse?.listaEmpresa?.find(e => e.idEmpresa === id);
    return empresa?.nomeEmpresa ?? '';
  }

  get saudacao(): string {
    const primeiroNome = (this.usuarioLoginResponse?.nomeUsuario ?? '').trim().split(/\s+/)[0];
    return primeiroNome ? `Olá, ${primeiroNome}!` : 'Olá!';
  }

  /** Empresas filtradas pela busca e agrupadas por grupo de empresas; as sem grupo vão para "Outras empresas". */
  get gruposEmpresa(): GrupoEmpresaUi[] {
    const lista = this.usuarioLoginResponse?.listaEmpresa ?? [];
    const termo = this.filtroEmpresa().trim().toLowerCase();
    const termoDigitos = termo.replace(/\D/g, '');

    const filtradas = lista.filter(empresa => {
      if (!termo) {
        return true;
      }
      const nome = (empresa.nomeEmpresa ?? '').toLowerCase();
      const fantasia = (empresa.nomeFantasia ?? '').toLowerCase();
      const cnpj = (empresa.cnpj ?? '').replace(/\D/g, '');
      return (
        nome.includes(termo) || fantasia.includes(termo) || (!!termoDigitos && cnpj.includes(termoDigitos))
      );
    });

    const porGrupo = new Map<string, EmpresaAuthStruct[]>();
    const semGrupo: EmpresaAuthStruct[] = [];

    for (const empresa of filtradas) {
      if (empresa.nomeGrupoEmpresa) {
        const grupo = porGrupo.get(empresa.nomeGrupoEmpresa) ?? [];
        grupo.push(empresa);
        porGrupo.set(empresa.nomeGrupoEmpresa, grupo);
      } else {
        semGrupo.push(empresa);
      }
    }

    const grupos: GrupoEmpresaUi[] = Array.from(porGrupo.entries()).map(([nome, empresas]) => ({
      nome,
      icone: 'account_tree',
      empresas,
    }));

    if (semGrupo.length) {
      grupos.push({
        nome: grupos.length ? 'Outras empresas' : 'Empresas',
        icone: 'domain',
        empresas: semGrupo,
      });
    }

    return grupos;
  }

  /** Iniciais para o avatar do card (ignora sufixos societários). */
  iniciais(nomeEmpresa: string): string {
    const partes = (nomeEmpresa ?? '')
      .replace(/\b(LTDA|ME|EPP|EIRELI|S\/A|SA)\b\.?/gi, '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    return ((partes[0]?.[0] ?? '') + (partes[1]?.[0] ?? '')).toUpperCase();
  }
}
